import axios from 'util/Api'
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import moment from "moment";
import { fetchError, fetchStart, fetchSuccess } from "./Common";
import {
  GET_RECONCILIATION_ASSETS_BY_VOUCHER_SUCCESS,
  PROCESS_RECONCILIATION_ASSETS_SUCCESS,
  PROCESS_RECONCILIATION_PV_ASSETS_SUCCESS,
  SAVE_RECONCILIATION_ASSETS_SUCCESS,
  SAVE_RECONCILIATION_PV_ASSETS_SUCCESS,
  SAVE_RECONCILIATION_ASSET_ASSETS_SUCCESS,
  PROCESS_RECONCILIATION_ASSET_ASSETS_SUCCESS
} from "../../constants/ActionTypes";

// Which success action to dispatch for a given source: ast | pv | asset.
const saveType = (recType) => recType.toUpperCase() === 'AST' ? SAVE_RECONCILIATION_ASSETS_SUCCESS
  : recType.toUpperCase() === 'PV' ? SAVE_RECONCILIATION_PV_ASSETS_SUCCESS
    : SAVE_RECONCILIATION_ASSET_ASSETS_SUCCESS;
const processType = (recType) => recType.toUpperCase() === 'AST' ? PROCESS_RECONCILIATION_ASSETS_SUCCESS
  : recType.toUpperCase() === 'PV' ? PROCESS_RECONCILIATION_PV_ASSETS_SUCCESS
    : PROCESS_RECONCILIATION_ASSET_ASSETS_SUCCESS;

export const getAssetsByAuditVoucher = (auditVoucher, filterLocation, filterClient, filterStatus, filterAssetType, filterSubType, filterModel) => {
  return async dispatch => {
    dispatch(fetchStart());
    try {
      const response = await axios.post(`/reconciliation/reconcile/${auditVoucher}`, {
        location: filterLocation, client: filterClient, status: filterStatus,
        assetType: filterAssetType, equipmentSubType: filterSubType, assetModel: filterModel
      });
      // console.log(response.data);
      dispatch(getAssetsByAuditVoucherSuccess(response));
      dispatch(fetchSuccess());
    } catch (error) {
      dispatch(fetchError(error));
      console.log("Error****:", error);
    }
  };
};

export const getAssetsByAuditVoucherSuccess = ({ data }) => {
  return {
    type: GET_RECONCILIATION_ASSETS_BY_VOUCHER_SUCCESS,
    data
  };
};

// Resolves to { ok: true, data } or { ok: false, error }, so a caller can wait for the
// save before going on (ReconciliationV3 process() saves pending changes first).
// Existing callers that ignore the result are unaffected.
export const saveReconciliationData = (payload, voucher, recType = 'ast') => {
  // console.log(payload);
  return async dispatch => {
    dispatch(fetchStart());
    try {
      if (payload && payload.length) {
        payload.forEach(p => {
          p.reconciliationSource = recType.toUpperCase()
          p.auditVoucher = voucher
        })
      }
      const response = await axios.post(`/reconciliation/save-action/${voucher}`, payload);
      // console.log(response);

      dispatch(fetchSuccess());
      dispatch(saveDataSuccess(response.data, recType));
      return { ok: true, data: response.data };
    } catch (error) {
      dispatch(fetchError(error));
      console.log("Error****:", error);
      const data = error && error.response && error.response.data;
      const text = (data && (data.message || data.error)) || (error && error.message) || 'request failed';
      return { ok: false, error: Array.isArray(text) ? text.join('; ') : String(text) };
    }
  };
};

export const saveDataSuccess = (data, recType) => {
  return {
    type: saveType(recType),
    data
  };
};

// Resolves to { ok: true, data } or { ok: false, error } so a caller sending several
// requests in turn (ReconciliationV3 process(), in chunks) can report each one.
// `options.quiet`: a failure is returned to the caller only, not also put in
// common.error (which the cockpit shows as "Processing was not accepted").
export const processReconciliationData = (payload, voucher, recType = 'AST', options = {}) => {
  // console.log(payload);
  return async dispatch => {
    dispatch(fetchStart());
    try {
      if (payload && payload.length) {
        payload.forEach(p => {
          p.reconciliationSource = recType.toUpperCase()
          p.auditVoucher = voucher
        })
      }
      const response = await axios.post(`/reconciliation/process/${voucher}`, payload);
      // console.log(response);

      dispatch(fetchSuccess());
      dispatch(processDataSuccess(response.data, recType));
      return { ok: true, data: response.data };
    } catch (error) {
      if (options.quiet) dispatch(fetchSuccess());
      else dispatch(fetchError(error));
      console.log("Error****:", error);
      const data = error && error.response && error.response.data;
      const text = (data && (data.message || data.error)) || (error && error.message) || 'request failed';
      return { ok: false, error: Array.isArray(text) ? text.join('; ') : String(text) };
    }
  };
};

export const processDataSuccess = (data, recType) => {
  return {
    type: processType(recType),
    data
  };
};


/**
 * Writes already-shaped rows to a multi-sheet workbook.
 *
 * The Cockpit builds its own rows because only the container knows the labels the
 * user actually saw - the grouped reconcile status, the action label, the decision
 * state, the asset's status in the audit and the scope that produced the missing
 * count. exportReconciliationData below stays as-is for the legacy containers,
 * which feed it their own tab-shaped data.
 *
 * @param sheets   [{ name, rows: [flat objects] }]
 * @param filename without extension
 */
export const exportSheets = (sheets, filename = 'reconciliation') => {
  const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  const wb = { Sheets: {}, SheetNames: [] }
  ;(sheets || []).forEach(({ name, rows }) => {
    wb.Sheets[name] = XLSX.utils.json_to_sheet(rows && rows.length ? rows : [{}])
    wb.SheetNames.push(name)
  })
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
  FileSaver.saveAs(new Blob([buf], { type: fileType }), `${filename}-${Date.now()}.xlsx`)
}

export const exportReconciliationData = (data, locations, clients) => {
  const { astAssets, allAssets, pvAssets } = data || {};
  // console.log(data);

  const tableData = [...astAssets];

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';

  let arr1 = [];
  let arr2 = [];
  let arr3 = [];

  if (tableData && tableData.length) {

    for (let i = 0; i < tableData.length; i++) {
      const { assetCode, astNo, location, client, reconcileStatus, matchedAsset, actionTaken, processedRemark, isProcessed, isAlreadySaved } = tableData[i];
      const locIndex = locations.findIndex(loc => loc._id === location);
      const clIndex = clients.findIndex(cl => cl._id === client);

      let locMIndex = -1;
      let clMIndex = -1
      if (matchedAsset && matchedAsset.location)
        locMIndex = locations.findIndex(loc => loc._id === matchedAsset.location);
      if (matchedAsset && matchedAsset.client)
        clMIndex = clients.findIndex(cl => cl._id === matchedAsset.client);

      let obj = {};
      obj.Sno = i + 1;
      obj['Asset'] = assetCode;
      obj['AST'] = astNo;
      obj['Location'] = locIndex >= 0 && locations[locIndex]['name'];
      obj['SubLocation'] = clIndex >= 0 && clients[clIndex]['name'];
      obj['Status'] = reconcileStatus;
      obj['Action'] = actionTaken && actionTaken.name;
      obj['is_saved'] = isAlreadySaved ? 'Yes' : 'No';
      obj['is_processed'] = isProcessed ? 'Yes' : 'No'
      obj['processed_remark'] = processedRemark || '';
      obj['Matched_Asset'] = matchedAsset && matchedAsset['assetCode'];
      obj['Matched_Asset_Location'] = locMIndex >= 0 && locations[locMIndex]['name'];
      obj['Matched_Asset_SubLocation'] = clMIndex >= 0 && clients[clMIndex]['name'];

      arr1.push(obj);
    }
  }

  if (allAssets && allAssets.length) {

    for (let i = 0; i < allAssets.length; i++) {
      const { assetCode, location, client, ast, pv, astAssetCode, astLocation, astClient, original_status, status, processed_remark } = allAssets[i];
      const locIndex = locations.findIndex(loc => loc._id === location);
      const clIndex = clients.findIndex(cl => cl._id === client);

      let locMIndex = -1;
      let clMIndex = -1
      if (astLocation)
        locMIndex = locations.findIndex(loc => loc._id === astLocation);
      if (astClient)
        clMIndex = clients.findIndex(cl => cl._id === astClient);

      let obj = {};
      obj.Sno = i + 1;
      obj['Asset'] = assetCode;
      obj['Location'] = locIndex >= 0 && locations[locIndex]['name'];
      obj['SubLocation'] = clIndex >= 0 && clients[clIndex]['name'];
      obj['Status'] = original_status

      obj['Action'] = status && status.name;
      obj['AST'] = ast && ast.astNo;
      obj['PV'] = pv && pv.pvNo;

      obj['ProcessRemark'] = processed_remark;


      if (obj['Location'] !== 'SOLD')
        arr2.push(obj);
    }
  }

  if (pvAssets && pvAssets.length) {

    for (let i = 0; i < pvAssets.length; i++) {
      const { tempAssetCode, location, client, pvNo, assetModel, assetType, equipmentSubType, reconcileStatus, actionTaken, isAlreadySaved, isProcessed, processedRemark } = pvAssets[i];
      const locIndex = locations.findIndex(loc => loc._id === location);
      const clIndex = clients.findIndex(cl => cl._id === client);

      let obj = {};
      obj.Sno = i + 1;
      obj['TempAsset'] = tempAssetCode;
      obj['Location'] = locIndex >= 0 && locations[locIndex]['name'];
      obj['SubLocation'] = clIndex >= 0 && clients[clIndex]['name'];
      obj['PV'] = pvNo;
      obj['AssetModel'] = assetModel && assetModel.name;
      obj['SubType'] = equipmentSubType && equipmentSubType.name;
      obj['AssetType'] = assetType && assetType.name;
      obj['Status'] = reconcileStatus;
      obj['Action'] = actionTaken && actionTaken.name;
      obj['is_saved'] = isAlreadySaved ? 'Yes' : 'No';
      obj['is_processed'] = isProcessed ? 'Yes' : 'No'
      obj['processed_remark'] = processedRemark || '';

      arr3.push(obj);
    }
  }

  const ws1 = XLSX.utils.json_to_sheet(arr1);
  const ws2 = XLSX.utils.json_to_sheet(arr2);
  const ws3 = XLSX.utils.json_to_sheet(arr3);

  const wb = { Sheets: { 'ast': ws1, 'assets': ws2, 'pv': ws3 }, SheetNames: ['ast', 'assets', 'pv'] };

  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const xls_data = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(
    xls_data,
    `reconciliation-${Date.now()}${fileExtension}`,
  );

};